import React, { useEffect, memo, useState, useMemo } from 'react';
import ModalWithPattern from './ModalWithPattern';
import Carousel from '../Carousel';
import SvgQuestion from '../../../atoms/icons/SvgQuestion';
import styled from '@emotion/styled';
import { isDarkMode, IsMobileViewPort } from '../../../utils/helpers';
import { mixpanelTrackWhatsAnOfferModalShown } from '../../../utils/mixpanel/infoModalShown';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { getTutorialData } from '../../../modules/QuickOfferModal/TutorialData';
import { getBackgroundTheme } from 'style';
import { FONT_FAMILIES } from 'theme/material/constants';

const TutorialContainer = styled('div')({
  display: 'initial',
  verticalAlign: 'middle',
});

const StyledSpan = styled('span')({
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: FONT_FAMILIES.primary,
  '&:hover': {
    textDecoration: 'underline',
    color: '#2490DE',
  },
});

const mobileOverlayStyle = {
  borderRadius: 10,
  height: '100%',
  width: '90%',
  padding: '0 10%',
  left: '-5%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
};

const mobileContentStyle = {
  borderRadius: 10,
  maxWidth: 355,
  maxHeight: 575,
  top: '50%',
  height: 'auto',
  transform: 'translate(0, -50%)',
  background: getBackgroundTheme().block2,
  border: `1px solid ${isDarkMode() ? '#848484' : '#FFFFFF'}`,
};

interface OffersTutorialProps {
  hideArrows?: boolean;
  clickableIndicators?: boolean;
  iconOnly?: boolean;
  linkStyle?: Record<string, unknown>;
  showTutorial?: boolean;
  onClose(): void;
  isFirstTimeOffer?: boolean;
  svgIconHeight?: number;
  svgIconWeight?: number;
  svgIconFill?: string;
}

const OffersTutorialModal = ({
  iconOnly = false,
  hideArrows = true,
  clickableIndicators = true,
  linkStyle = {},
  showTutorial = false,
  onClose,
  isFirstTimeOffer = false,
  svgIconHeight = 12,
  svgIconWeight = 12,
  svgIconFill,
}: OffersTutorialProps): JSX.Element => {
  const [showOffersTutorial, setShowOffersTutorial] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const profile = useSelector((state: RootState) => state.profile);

  const isAttractive = useMemo(
    () => profile?.profile?.data.account_type === 'Attractive',
    [profile?.profile?.data.account_type]
  );

  useEffect(() => {
    setShowOffersTutorial(showTutorial);

    () => {
      setShowOffersTutorial(false);
    };
  }, [showTutorial]);

  const handleOnLinkClick = e => {
    e.preventDefault();

    if (!showOffersTutorial) {
      setShowOffersTutorial(true);
      mixpanelTrackWhatsAnOfferModalShown('Discover Mode');
    }
  };

  const handleCloseModal = () => {
    setShowOffersTutorial(false);
    onClose();
  };

  return (
    <TutorialContainer>
      <StyledSpan
        style={linkStyle}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={e => handleOnLinkClick(e)}
      >
        <SvgQuestion
          viewBox="0 0 512 500"
          styles={{
            fill: svgIconFill ? svgIconFill : isHover ? '#2490DE' : isDarkMode() ? '#AFB0B1' : '#8F8F8F',
            height: svgIconHeight,
            width: svgIconWeight,
          }}
        />{' '}
        {iconOnly ? '' : 'What is an Offer?'}
      </StyledSpan>
      <ModalWithPattern
        dataTestID="offers-tutorial-modal"
        isModalOpen={showOffersTutorial}
        modalContentLabel="Offers Tutorial"
        closeOnOverlayClick
        closeRequest={handleCloseModal}
        closeBtnClassName="offers-tutorial-close"
        closeBtnDataTestId="offers-tutorial-close"
        onCloseClick={handleCloseModal}
        customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
        customContentStyle={
          IsMobileViewPort
            ? mobileContentStyle
            : { background: getBackgroundTheme().block2, borderWidth: 1, maxWidth: 450, maxHeight: 498 }
        }
      >
        <Carousel
          slidesData={getTutorialData(isAttractive, {})}
          hideArrows={hideArrows}
          clickableIndicators={clickableIndicators}
          onClose={handleCloseModal}
          isFirstTimeOffer={isFirstTimeOffer}
        />
      </ModalWithPattern>
    </TutorialContainer>
  );
};

export default memo(OffersTutorialModal);
